import Lightbox from "lightbox-react";
import { debounce, get, isEqual } from "lodash";
import { FC, memo, useCallback, useEffect, useMemo, useState } from "react";
import { Link, useRouteMatch } from "react-router-dom";
import Tooltip from "react-tooltip-lite";
import { useBoolean } from "usehooks-ts";
import { InventoryItem, useUpdateCustomItemMutation, useUpdateInventoryItemMutation } from "../../Generated/graphql";
import { getImageWithSize } from "../../Utils/getImageWithSize";
import { ExitButton } from "../ExitButton/ExitButton";
import { Icon } from "../Icon/Icon";
import { ImageTooltip } from "../ImageTooltip/ImageTooltip";
import { ItemAddedToCart } from "../ItemAddedToCart/ItemAddedToCart";
import { ItemOrdered } from "../ItemOrdered/ItemOrdered";
import { ListItemActions } from "../ListItemActions/ListItemActions";
import { ProgressBar } from "../ProgressBar/ProgressBar";
import "./InventoryListItem.css";

type Props = {
  windowWidth: number;
  item: InventoryItem;
  canEdit: boolean;
};

const InventoryListItem: FC<Props> = ({ canEdit, item, windowWidth }) => {
  const { value: showImage, setValue: setShowImage } = useBoolean(false);
  const [photoIndex, setPhotoIndex] = useState(0);
  const [optimisticAmount, setOptimisticAmount] = useState(item.amount);
  const [isUpdating, setIsUpdating] = useState(false);
  const [hasError, setHasError] = useState(false);
  const match = useRouteMatch();
  
  const [updateInventoryItem] = useUpdateInventoryItemMutation();
  const [updateCustomItem] = useUpdateCustomItemMutation();

  // Reset optimistic amount when item amount changes (from external updates)
  useEffect(() => {
    setOptimisticAmount(item.amount);
    setHasError(false);
  }, [item.amount]);

  const allImages = useMemo(() => {
    return item.images.reduce<string[]>((acc, image) => {
      if (!image?.url) {
        return acc;
      }
      const imageUrl = getImageWithSize(image?.url, "800x800");
      acc.push(imageUrl);
      return acc;
    }, []);
  }, [item.images]);

  const unitAsText = item.amountPerUnit > 1 ? "ks." : "stk.";

  const debouncedUpdate = useMemo( () =>
    debounce(async (newAmount: number) => {
      setIsUpdating(true);
      try {
        if (item.isCustom) {
          await updateCustomItem({
            variables: {
              updateCustomItemId: item.productNumber,
              barcode: item.barcode,
              category: item.category,
              description: item.description,
              inStock: newAmount,
              name: item.name,
              price: item.price,
              supplier: item.supplier,
              units: item.unit,
            },
          });
        } else {
          await updateInventoryItem({
            variables: {
              productNumber: item.productNumber,
              action: "manual",
              automaticOrdering: item.automaticOrderingState,
              inStock: newAmount,
              maximum: item.maximum,
              minimum: item.minimum,
            },
          });
        }
      } catch (error) {
        setOptimisticAmount(item.amount);
        setHasError(true);
        // Clear error after 3 seconds
        setTimeout(() => setHasError(false), 3000);
      } finally {
        setIsUpdating(false);
      }
    }, 500),    
    [item.productNumber, item.isCustom, item.amount, item.barcode, item.category, item.description, item.name, item.price, item.supplier, item.unit, item.automaticOrderingState, item.maximum, item.minimum, updateInventoryItem, updateCustomItem]
  );

  const handleIncrement = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!canEdit) return;
    const newAmount = optimisticAmount + 1;
    setOptimisticAmount(newAmount);
    debouncedUpdate(newAmount);
  }, [optimisticAmount, canEdit, debouncedUpdate]);

  const handleDecrement = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!canEdit || optimisticAmount <= 0) return;
    const newAmount = optimisticAmount - 1;
    setOptimisticAmount(newAmount);
    debouncedUpdate(newAmount);
  }, [optimisticAmount, canEdit, debouncedUpdate]);

  return item ? (
    <>
      {showImage && allImages.length > 0 && (
        <Lightbox
          mainSrc={allImages[photoIndex]}
          nextSrc={allImages[(photoIndex + 1) % allImages.length]}
          prevSrc={
            allImages[(photoIndex + allImages.length - 1) % allImages.length]
          }
          onCloseRequest={() => setShowImage(false)}
          onMovePrevRequest={() =>
            setPhotoIndex(
              (photoIndex + allImages.length - 1) % allImages.length
            )
          }
          onMoveNextRequest={() =>
            setPhotoIndex((photoIndex + 1) % allImages.length)
          }
        />
      )}
      <Link
        to={{
          pathname: `${match.path}/edit/${item.productNumber}`,
        }}
        className={`InventoryListItem`}
      >
        <div className="InventoryListItem__content">
          {windowWidth > 1023 && (
            <Tooltip
              arrow={false}
              distance={0}
              direction="right"
              tipContentHover={true}
              content={
                <div className="Tooltip__content">
                  {navigator.userAgent.match(/iPad/i) && (
                    <div
                      style={{
                        position: "absolute",
                        top: "6px",
                        right: "6px",
                      }}
                    >
                      <ExitButton text="" solidBack={true} />
                    </div>
                  )}
                  {item.images[0] ? (
                    <ImageTooltip imageUrl={item.images[0].url} />
                  ) : (
                    <Icon name="noImageAvail" width={200} />
                  )}
                </div>
              }
            >
              <div className="InventoryListItem__image-icon">
                <Icon name="preview" width={20} />
                <div
                  className="IntentoryListItem__hover-trigger"
                  onClick={(e) => {
                    e.preventDefault();
                    setShowImage(true);
                  }}
                />
              </div>
            </Tooltip>
          )}

          <div className="InventoryListItem__id u-cursor-default InventoryListItem__text">
            {item.productNumber}
          </div>
          <div className="InventoryListItem__name u-cursor-default InventoryListItem__text">
            <div className="InventoryListItem__text-formatted">
              {get(item, "name", " ")}
            </div>
            {((item.dataSheets && item.dataSheets.length > 0) || (item.dataSheetDetails && item.dataSheetDetails.length > 0)) && (
              <Icon name="infoIcon" width={12} height={12} className="InventoryListItem__datasheet-indicator" title="Datablade tilgængelige" />
            )}
          </div>
          {windowWidth > 1023 && (
            <div className="InventoryListItem__details u-cursor-default InventoryListItem__text">
              <div className="InventoryListItem__text-formatted">
                {get(item, "description", " ")}
              </div>
            </div>
          )}
          {item.alreadyOrdered ? (
            <ItemOrdered windowWidth={windowWidth} />
          ) : item.inCart ? (
            <ItemAddedToCart windowWidth={windowWidth} />
          ) : (
            <div />
          )}
          {windowWidth > 1023 && (
            <div className="InventoryListItem__stock-amount u-cursor-default">
              {canEdit ? (
                <div 
                  className={`InventoryListItem__stock-controls ${hasError ? 'InventoryListItem__stock-controls--error' : ''}`} 
                  onClick={(e) => e.preventDefault()}
                >
                  <button
                    className="InventoryListItem__stock-btn InventoryListItem__stock-btn--minus"
                    onClick={handleDecrement}
                    disabled={optimisticAmount <= 0 || isUpdating}
                  >
                    <Icon name="subtractRed" width={12} />
                  </button>
                  <span className={`InventoryListItem__stock-value ${isUpdating ? 'InventoryListItem__stock-value--updating' : ''}`}>
                    {optimisticAmount} {unitAsText}
                  </span>
                  <button
                    className="InventoryListItem__stock-btn InventoryListItem__stock-btn--plus"
                    onClick={handleIncrement}
                    disabled={isUpdating}
                  >
                    <Icon name="cardAdd" width={12} />
                  </button>
                </div>
              ) : (
                <span>{item.amount ?? 0} {unitAsText}</span>
              )}
            </div>
          )}
          <div className="InventoryListItem__bar u-cursor-default">
            <ProgressBar
              current={optimisticAmount}
              min={item.minimum}
              max={item.maximum}
            />
          </div>
          <div className="InventoryListItem__refresh u-cursor-default">
            {" "}
            {/* The fuk is this class name? */}
            {item.automaticOrderingState === "active" ? (
              <Icon name="autoOrderActive" width={16} />
            ) : item.automaticOrderingState === "deactivated" ? (
              <Icon name="autoOrderDisabled" width={19} />
            ) : (
              <Icon
                className="disabledAutomaticOrdering"
                name="autoOrderDisabled"
                width={19}
              />
            )}
          </div>
          <div className="InventoryListItem__actions u-cursor-pointer">
            <ListItemActions
              windowWidth={windowWidth}
              canEdit={canEdit}
              add={() => {}}
              remove={() => {}}
              id={item.productNumber}
              toggleEdit={() => {}}
            />
          </div>
        </div>
      </Link>
    </>
  ) : null;
};

const memoized = memo(InventoryListItem, (prev, next) => {
  /** Is everything the same? */
  if (
    isEqual(prev.item, next.item) &&
    prev.canEdit === next.canEdit &&
    prev.windowWidth === next.windowWidth
  ) {
    return true;
  }

  return false;
});

export { memoized as InventoryListItem };
